import {
  IconCalendar,
  IconInfoCircleFilled,
  IconPound,
  IconStarOutline,
} from '@iwoca/icons';
import { GetApplicationResponse } from '@iwoca/lapi-client/edge';

import styles from './LoanSummary.module.css';
import { getDurationInYears } from '../../../../helpers/product.helpers';
import { useNumberFormat } from '../../../../hooks/useNumberFormat';
import { TGetOffersResponse } from '../../../../types';

type TProps = {
  application: GetApplicationResponse['data'];
  offers: TGetOffersResponse['data']['offers'];
};

const DEFAULT_MAX_TERM_YEARS = 5;

export const LoanSummary = ({ application, offers }: TProps) => {
  const { formatCurrency } = useNumberFormat();

  const requestedAmount = application?.requested_products?.[0].amount;

  const estimatedAmount = offers.find(
    (offer) => offer.is_suggested,
  )?.max_amount;

  const isEstimatedAmountSmaller =
    !!estimatedAmount && Number(estimatedAmount) < Number(requestedAmount);
  const showRequestedAmount = !estimatedAmount || isEstimatedAmountSmaller;

  const offerDurationsInYears = offers.map((offer) =>
    getDurationInYears(offer.duration),
  );

  const maxTermLength =
    offerDurationsInYears.length > 0
      ? Math.max(...offerDurationsInYears)
      : DEFAULT_MAX_TERM_YEARS;

  return (
    <div className={styles.container}>
      <div className={styles.loanDetailsContainer}>
        <div className={styles.productTypeContainer}>
          You’re applying for:
          <div className={styles.productType}>Flexi-Loan</div>
        </div>
        <ul className={styles.loanDetailsList}>
          <li className={styles.loanDetail}>
            <IconCalendar
              data-testid="calendar-icon"
              width={20}
              height={20}
              className={styles.listIcon}
            />
            Term length:
            <span className={styles.loanDetailValue}>
              From 1 day to {maxTermLength} years
            </span>
          </li>
          <li className={styles.loanDetail}>
            <IconPound
              data-testid="pound-icon"
              width={20}
              height={20}
              className={styles.listIcon}
            />
            {showRequestedAmount ? (
              <span>Requested amount:</span>
            ) : (
              <span>Estimated amount:</span>
            )}
            <span className={styles.loanDetailValue}>
              {formatCurrency(
                showRequestedAmount ? requestedAmount : estimatedAmount,
              )}
            </span>
          </li>
          <li className={styles.loanDetail}>
            <IconStarOutline
              data-testid="starOutline-icon"
              width={20}
              height={20}
              className={styles.listIcon}
            />
            Repay early for free
          </li>
        </ul>
      </div>
      <div className={styles.complianceText}>
        <IconInfoCircleFilled width={24} height={24} className={styles.icon} />
        Submit key documents for a decision within one business day. No impact
        on credit score. Loan amount may change based on information provided,
        and approval not guaranteed.
      </div>
    </div>
  );
};
